import React, { useContext, useEffect, useState } from 'react';
import { Col, Layout, Row, Input, Select, DatePicker } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { capitalize, get, omit } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

import ResellerServices from 'services/reseller';

import { TableList } from 'components/table-list';
import PdButton from 'components/pd-button';
import StatusList from 'components/list-status';

import { ProfileContext } from 'contexts/profile';

import { errHandling } from 'utils';

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

export function StatusResellers() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [activeList, setActiveList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [endDatePick, setEndDate] = useState('');
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [startDatePick, setStartDate] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const dateFormat = 'YYYY/MM/DD';

  function customElement(row) {
    return (
      <div className="button-table">
        <Link to={`/resellers_status/detail?id=${row.id}`}>
          <PdButton type="primary" style={{ height: 32, width: 100 }}>
            Detail
          </PdButton>
        </Link>
      </div>
    );
  }

  customElement.displayName = 'customButton';

  const [columns] = useState([
    {
      name: 'Name',
      selector: row => row.first_name,
      format: (row) =>
        `${get(row, 'first_name', '')} ${get(row, 'last_name', '')}`,
      width: '16%',
    },
    {
      name: 'Status',
      selector: row => row.application_status,
      format: (row) => capitalize(get(row, 'application_status', '')),
      width: '16%',
    },
    {
      name: 'Store Name',
      selector: row => row.store_name,
      width: '16%',
    },
    {
      name: 'Type',
      selector: row => row.reseller_type,
      format: (row) => capitalize(get(row, 'reseller_type', '')),
      width: '16%',
      center: true,
    },
    {
      name: 'Applied at',
      selector: row => row.register_at,
      format: (row) => {
        return dayjs(row.register_at).format('DD/MM/YYYY');
      },
      width: '16%',
      center: true,
    },
    {
      name: '',
      cell: customElement,
      center: true,
      width: '20%',
    },
  ]);

  async function fetchResellers({
    endDate,
    page,
    q,
    reseller_type,
    startDate,
    status,
  }) {
    setLoadStatus(true);
    try {
      let end = endDate;
      if (endDate) {
        end = dayjs(endDate, dateFormat).add(1, 'days');
        end = dayjs(end).format(dateFormat);
      }
      const { data } = await ResellerServices.getResellerList({
        endDate: end,
        page,
        q,
        reseller_type,
        startDate,
        status,
      });
      const resellerData = get(data, 'data', []);
      const cursor = get(data, 'metadata.next_cursor', -1);
      setActiveList(resellerData);
      setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_status',
      search: stringified,
    });
  }

  function onChangeDate(_, [start, end]) {
    if (start === '') delete parsed['start_date'];
    else parsed['start_date'] = start;

    if (end === '') delete parsed['end_date'];
    else parsed['end_date'] = end;

    parsed['page'] = 1;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_status',
      search: stringified,
    });
  }

  function filterReseller(param, val) {
    if (!val) delete parsed[param];
    else parsed[param] = val;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_status',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, [
        'application_status',
        'end_date',
        'limit',
        'page',
        'q',
        'reseller_type',
        'start_date',
      ])
    );
    navigate({
      pathname: '/resellers_status',
      search: stringified,
    });
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['q']) setSearchValue(parsed['q']);
    else setSearchValue('');

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/resellers_status',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['application_status']) setStatus(parsed['application_status']);
    else setStatus('');

    if (parsed['reseller_type']) setType(parsed['reseller_type']);
    else setType('');

    if (parsed['start_date']) setStartDate(parsed['start_date']);
    else setStartDate('');

    if (parsed['end_date']) setEndDate(parsed['end_date']);
    else setEndDate('');

    fetchResellers({
      endDate: parsed['end_date'],
      limit: 10,
      page: parsed['page'],
      q: parsed['q'],
      reseller_type: parsed['reseller_type'],
      startDate: parsed['start_date'],
      status: parsed['application_status'],
    });
  }, [
    parsed['end_date'],
    parsed['page'],
    parsed['q'],
    parsed['reseller_type'],
    parsed['start_date'],
    parsed['application_status'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">Reseller Status</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col span={14}>
              <Search
                className="search-product"
                enterButton
                onChange={(event) =>
                  setSearchValue(get(event, 'target.value', event))
                }
                onSearch={filterReseller.bind(this, 'q')}
                placeholder="Search Reseller"
                value={searchValue}
              />
            </Col>
            <Col span={4} offset={5} className="height-32">
              <div className="invisible">invisible</div>
            </Col>
            <Col span={7} className="mt-1">
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={onChangeDate}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
            <Col span={3} className="mt-1">
              <Select
                value={type}
                style={{ width: '100%' }}
                onChange={filterReseller.bind(this, 'reseller_type')}>
                <Option value="" selected hidden>
                  Type
                </Option>
                <Option value="dealer">Dealer</Option>
                <Option value="individual">Individual</Option>
                <Option value="buyer">Buyer</Option>
              </Select>
            </Col>
            <Col span={4} className="mt-1">
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>

            <Col span={24}>
              <Col span={4} className="mt-2">
                <span>Filter by Status:</span>
              </Col>
              <Col span={17} className="mt-1">
                <StatusList
                  onChange={filterReseller.bind(this, 'application_status')}
                  selectedStatus={status}
                  statusList={['', 'applied', 'rejected', 'accepted', 'incomplete']}
                />
              </Col>
            </Col>

            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={activeList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { StatusResellers };
